import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _LogUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Do NOT import other abweb classes, as if they internally use LOG then will get cycles!
import { AbConsoleLogger } from "abweb/utils/log/AbConsoleLogger";
import { InMemoryLogger } from "abweb/utils/log/InMemoryLogger";
import { LogLevel } from "abweb/utils/log/LogLevel";
import { addAppender, getLogger } from "aurelia-logging";
let BUILT_IN_LOG = undefined; // Can not use until after static init block completes

export class LogUtils {
  /** Process log.json that is used to configure logging behavior (_logPatterns and _defaultLogLevel) */
  static async fetchAndProcessLogJson() {
    // Deliberately use window.fetch and not import, because we do not want log.json to be in a chunk.
    // We cannot use FetchUtils because it imports LogUtils!
    try {
      // Set the Sec-Fetch-Site to same-origin, but the default Cache-Control is no-cache, so leave that alone.
      const init = {
        credentials: "same-origin"
      };
      const logProps = await window.fetch("webapp/abweb/assets/log/log.json", init).then(value => {
        if (value.ok && value.headers.get("content-type")?.includes("application/json")) {
          // yes, we got json, so attempt to parse it.
          return value.json();
        }
        return Promise.reject(`${value.status} (${value.statusText})`);
      });
      LogUtils.logLevel = logProps["DEFAULT_LOG_LEVEL"] != null ? LogLevel[logProps["DEFAULT_LOG_LEVEL"].toLowerCase()] // <any> due to string-to-enum
      : LogUtils._defaultLogLevel;

      // go through the defaults, add the patterns
      for (let pattern in logProps) {
        if (!logProps.hasOwnProperty(pattern) || pattern.charAt(0) === "_") {
          // skip comments and non-local properties
          continue;
        }
        const value = LogLevel[logProps[pattern].toLowerCase()];
        if (pattern === "DEFAULT_LOG_LEVEL") {
          pattern = ""; // make it the shortest, so length sort below matches LAST
          LogUtils._defaultLogLevel = value;
        }
        LogUtils._logPatterns[pattern] = value;
      }
    } catch (err) {
      BUILT_IN_LOG.error(`Problem loading log properties, will default logging to DEBUG: ${err}`);
      LogUtils._defaultLogLevel = LogLevel.debug;
      LogUtils._logPatterns = {};
    } finally {
      // Recalcs registered loggers according to the
      // new configuration that was imported from log.json (logPatterns and defaultLogLevel).
      LogUtils.recalcLogLevelsForRegisteredLoggers();
      BUILT_IN_LOG.info("Finished logging configuration with default log level: " + LogUtils._defaultLogLevel);
    }
  }
  static trimName(name) {
    // webpack raw __filename is something like: "webapp/abweb/utils/DebugUtils.ts"
    // On windows, it uses backslashes.
    name = name.replace(/\\/g, "/");
    // If present, trim everything before webapp, and after the last .tsx, .ts, .js
    const matches = name.match(/.*webapp\/(.*)\.(tsx|ts|jsx|js)/); // do .tsx before .ts so longest match first!
    return matches != null && matches.length > 0 ? matches[1] : name;
  }
  static getLogger(modName) {
    // CAT-1793: getLogger will be called in a bundled deployment before the logging has been configured
    // (log.json not yet been processed),
    modName = LogUtils.trimName(modName);
    let pair = LogUtils._loggers[modName];
    if (pair == null)
      // not in cache?
      {
        const logger = getLogger(modName); // create it (defaults to level 'none')
        const longest = this.determineLogLevel(modName, logger);
        LogUtils._loggers[modName] = pair = {
          log: logger,
          len: longest
        }; // into cache
      }
    return pair.log;
  }
  static determineLogLevel(loggerName, logger) {
    // Set the log level based on existing patterns
    let longest = -1;
    for (const pattern in LogUtils._logPatterns) {
      if (pattern.length > longest && loggerName.search(pattern) >= 0) {
        // longest matching pattern wins
        logger.setLevel(LogUtils._logPatterns[pattern]);
        longest = pattern.length;
      }
    }
    if (longest === -1) {
      // set logger level or else aurelia defaults it to 'none'
      logger.setLevel(LogUtils._defaultLogLevel);
    }
    return longest;
  }
  static recalcLogLevelsForRegisteredLoggers() {
    // CAT-1793: In bundle, loggers may be created (via LogUtils.getLogger)
    // before async log configuration (log.json fetch) is complete,
    // in which case recalc log levels after log configuration completes

    // Go through existing loggers, and where new pattern is longer set their levels
    for (const loggerName of Object.keys(LogUtils._loggers)) {
      const pair = LogUtils._loggers[loggerName];
      pair.len = LogUtils.determineLogLevel(loggerName, pair.log);
    }
  }
  static get inMemoryLogger() {
    return LogUtils._inMemoryLogger;
  }

  /**
   * Get default log level
   * @returns LogLevel
   */
  static get logLevel() {
    return LogUtils._logLevel;
  }

  /**
   * Set default log level
   * @param value
   */
  static set logLevel(value) {
    LogUtils._logLevel = value;
    LogUtils.addPattern("", value); // Record it in the patterns, too
  }

  /**
   * Set logging level for modules that match the pattern
   * Longer patterns have more impact than shorter ones, so '/abweb/lib' takes precedence over '/abweb'
   * @param pattern - regular expression that matches module names (typically just modName)
   * @param value - a log level: { none, error, warn, info, debug }
   */
  static addPattern(pattern, value) {
    pattern = LogUtils.trimName(pattern);
    LogUtils._logPatterns[pattern] = value;
    this.recalcLogLevelsForRegisteredLoggers();
  }

  /**
   * @return Will limit the returned log text to numBytes if numBytes greater or equal to 0
   * (so if negative, gives all log content)
   */
  static getLoggerText(numBytesLimit = -1) {
    const messages = LogUtils.inMemoryLogger.messages;
    if (numBytesLimit < 0) {
      const answer = messages.map(item => item.toString()).join("\r\n");
      return answer;
    }

    // work backwards as log messages are in chronological order and want the last X records
    let log = "";
    let currentLength = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      const logStr = messages[i].toString() + "\r\n";
      currentLength += logStr.length;
      // yep, += is the most js efficient way for string concat according to:
      //   https://stackoverflow.com/questions/16696632/most-efficient-way-to-concatenate-strings-in-javascript
      if (currentLength < numBytesLimit) {
        log = logStr + log; // do not use += as that is log + logStr
      } else {
        break;
      }
    }
    return log;
  }
  constructor() {
    throw new Error("LogUtils is a static helper class!");
  }
}
_LogUtils = LogUtils;
_defineProperty(LogUtils, "_inMemoryLogger", void 0);
_defineProperty(LogUtils, "_logLevel", void 0);
_defineProperty(LogUtils, "_logPatterns", {});
// one row for each match for log names
_defineProperty(LogUtils, "_loggers", {});
// one row for each logger, len of longest
// If log 'mod name' does not match a registered pattern, default the logger to this level.
// Conservatively starts at debug, but can can change via later async log.json configuration
_defineProperty(LogUtils, "_defaultLogLevel", LogLevel.debug);
// @ts-ignore: noUnusedLocals
_defineProperty(LogUtils, "staticInitializer", async function () {
  // Read default level from JSON file.  If it's not there, then default level is debug.
  // CAT-503: if in production mode, set default level to info.
  if (process.env.NODE_ENV === "production") {
    _LogUtils._defaultLogLevel = LogLevel.info;
  }

  // Define 'appenders' in the sync block so that any loggers configured and used before/during
  // below async configuration will output to them (according to defaultLogLevel).
  // Add both a console and in-memory log appender
  addAppender(new AbConsoleLogger());
  _LogUtils._inMemoryLogger = new InMemoryLogger();
  addAppender(_LogUtils._inMemoryLogger);
  BUILT_IN_LOG = _LogUtils.getLogger(__filename);
  BUILT_IN_LOG.info("Finished sync logging configuration with default log level: " + _LogUtils._defaultLogLevel);

  // CAT-1793: Above this line is the 'static logging' configuration' that allows LogUtils.getLogger
  // calls to succeed (based on above calculated defaultLoglevels) before the below async configuration completes.
  // The async configuration recalcs the registered loggers logLevels on task completion.
  if (window.hasOwnProperty("Cypress")) return;
  await _LogUtils.fetchAndProcessLogJson();
}());
//# sourceMappingURL=LogUtils.js.map
